import { useEffect, DependencyList } from 'react'

/**
 * A hook that allows using async functions within useEffect while properly handling
 * component lifecycles and cleanup.
 *
 * @param effect - An async function that can optionally return a cleanup function
 * @param deps - Dependency array that works like useEffect dependencies
 */
export const useAsyncEffect = (
  effect: () => Promise<(() => void) | void>,
  deps?: DependencyList
): void => {
  useEffect(() => {
    let isMounted = true
    let cleanupFunction: (() => void) | void = undefined

    // Execute the async effect and handle its result
    const executeAsyncEffect = async () => {
      try {
        const result = await effect()

        // Handle the cleanup function based on component mount status
        if (isMounted) {
          // Store cleanup function to be called later when the component unmounts
          cleanupFunction = result
        } else if (typeof result === 'function') {
          // Component already unmounted, call cleanup immediately
          result()
        }
      } catch (error) {
        // Only log errors if the component is still mounted
        if (isMounted) {
          // eslint-disable-next-line no-console
          console.error('Error in useAsyncEffect:', error)
        }
      }
    }

    // Start the async effect execution and ignore the returned promise
    void executeAsyncEffect()

    // Return the cleanup function that runs when deps change or component unmounts
    return () => {
      // Mark component as unmounted
      isMounted = false

      // Execute the cleanup function if it exists
      if (typeof cleanupFunction === 'function') {
        cleanupFunction()
      }
    }
  }, deps)
}
